/**
 * TeamLeaderboardCard — ★ Fase 14/16: classifica mensile dei venditori.
 *
 * ★ Fase 16 — visibile a TUTTI i venditori del team (per competere), oltre a
 * Direttrice e admin (tenant ES). Include:
 *  - banner del PREMIO del mese (impostato/modificato dalla Direttrice, con
 *    comunicazione email automatica al team);
 *  - popup automatico del premio (una volta per aggiornamento);
 *  - vincitore del mese precedente;
 *  - evidenziazione della propria riga («Tu»).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Trophy, Medal, RefreshCw, Gift, Crown, Pencil, Loader2 } from 'lucide-react';
import { useAuth } from '../../lib/auth-context';
import { useCrmTenant } from '../CrmTenantSwitcher';

const eur = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;

const RANK_STYLES = [
  'bg-amber-100 text-amber-800 border-amber-300',   // 1°
  'bg-slate-100 text-slate-600 border-slate-300',   // 2°
  'bg-orange-100 text-orange-800 border-orange-300', // 3°
];

const prizeSeenKey = (d) =>
  `gp_prize_seen_${d.tenant}_${d.year}_${d.month}_${d.prize?.updated_at || ''}`;

export function TeamLeaderboardCard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { tenant } = useCrmTenant();
  const isAdminLike = ['admin', 'amministrativo'].includes((user?.role || '').toLowerCase());
  const lockedTenant = user?.crm_tenant_locked || null;
  // director → proprio tenant; admin → solo vista ES; venditori → risolto dal backend
  const langParam = lockedTenant || (isAdminLike ? (tenant === 'es' ? 'es' : null) : undefined);
  const enabled = lockedTenant || !isAdminLike || tenant === 'es';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  // ★ Fase 16 — dialog premio (edit) + popup annuncio
  const [prizeOpen, setPrizeOpen] = useState(false);
  const [prizeTitle, setPrizeTitle] = useState('');
  const [prizeDesc, setPrizeDesc] = useState('');
  const [prizeNotify, setPrizeNotify] = useState(true);
  const [prizeSaving, setPrizeSaving] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const popupShownRef = useRef(false);

  const load = useCallback(() => {
    if (!enabled) return;
    setLoading(true);
    const params = langParam ? { language: langParam } : {};
    api.get('/sales-goals/leaderboard', { params })
      .then((r) => { setData(r.data); setHidden(false); })
      .catch(() => setHidden(true))
      .finally(() => setLoading(false));
  }, [enabled, langParam]);

  useEffect(() => { load(); }, [load]);

  // ★ Fase 16 — popup automatico del premio (una volta per aggiornamento)
  useEffect(() => {
    if (!data?.prize || popupShownRef.current) return;
    const key = prizeSeenKey(data);
    try {
      if (!localStorage.getItem(key)) {
        popupShownRef.current = true;
        setPopupOpen(true);
      }
    } catch { /* storage non disponibile */ }
  }, [data]);

  if (!enabled || hidden) return null;

  const items = data?.items || [];
  const myEmail = (data?.me_email || '').toLowerCase();
  const myIndex = items.findIndex((r) => (r.email || '').toLowerCase() === myEmail);

  const closePopup = () => {
    setPopupOpen(false);
    if (data?.prize) {
      try { localStorage.setItem(prizeSeenKey(data), '1'); } catch { /* noop */ }
    }
  };

  const openPrizeEdit = () => {
    setPrizeTitle(data?.prize?.title || '');
    setPrizeDesc(data?.prize?.description || '');
    setPrizeNotify(true);
    setPrizeOpen(true);
  };

  const savePrize = async () => {
    if (prizeTitle.trim().length < 2) {
      toast.error(t('leaderboard.prizeTitleRequired', 'Dai un nome al premio'));
      return;
    }
    setPrizeSaving(true);
    try {
      const r = await api.put('/sales-goals/prize', {
        title: prizeTitle.trim(),
        description: prizeDesc.trim(),
        notify: prizeNotify,
        language: langParam || undefined,
      });
      const n = r.data?.notified || 0;
      toast.success(prizeNotify
        ? t('leaderboard.prizeSavedNotified', 'Premio salvato: comunicazione inviata al team') + ` (${n})`
        : t('leaderboard.prizeSaved', 'Premio del mese salvato'));
      setPrizeOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('leaderboard.prizeError', 'Errore salvataggio premio'));
    } finally {
      setPrizeSaving(false);
    }
  };

  return (
    <Card className="border-border" data-testid="team-leaderboard-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Trophy className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
            <span className="text-sm font-medium truncate">
              {t('leaderboard.title', 'Classifica del mese')}
              <span className="text-muted-foreground font-normal"> · {data?.month_label || ''}</span>
              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded px-1 py-0.5">
                {(data?.tenant || '').toUpperCase()}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {data?.prize && (
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setPopupOpen(true)}
                data-testid="leaderboard-prize-popup-btn" aria-label={t('leaderboard.prize', 'Premio del mese')}>
                <Gift className="w-4 h-4 text-[var(--gp-gold)]" />
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={load} data-testid="leaderboard-refresh" aria-label={t('common.refresh', 'Aggiorna')}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* ★ Fase 16 — banner premio del mese */}
        {(data?.prize || data?.can_edit_prize) && (
          <div
            className="rounded-md border border-[var(--gp-gold)]/60 bg-[var(--gp-gold)]/10 px-3 py-2 flex items-start justify-between gap-2"
            data-testid="leaderboard-prize-banner"
          >
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--primary))] flex items-center gap-1">
                <Gift className="w-3 h-3" /> {t('leaderboard.prize', 'Premio del mese')}
              </div>
              {data?.prize ? (
                <>
                  <div className="text-sm font-semibold truncate" data-testid="leaderboard-prize-title">{data.prize.title}</div>
                  {data.prize.description && (
                    <div className="text-[11px] text-muted-foreground line-clamp-2">{data.prize.description}</div>
                  )}
                </>
              ) : (
                <div className="text-xs text-muted-foreground italic">
                  {t('leaderboard.noPrize', 'Nessun premio impostato per questo mese.')}
                </div>
              )}
            </div>
            {data?.can_edit_prize && (
              <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={openPrizeEdit} data-testid="leaderboard-prize-edit-btn">
                <Pencil className="w-3 h-3 mr-1" /> {t('leaderboard.editPrize', 'Premio')}
              </Button>
            )}
          </div>
        )}

        {loading ? (
          <div className="space-y-2"><Skeleton className="h-8" /><Skeleton className="h-8" /><Skeleton className="h-8" /></div>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="leaderboard-empty">
            {t('leaderboard.empty', 'Nessun venditore attivo nel team.')}
          </p>
        ) : (
          <div className="space-y-1.5" data-testid="leaderboard-list">
            <div className="grid grid-cols-[2rem_1fr_auto_auto] gap-2 text-[10px] uppercase tracking-wide text-muted-foreground px-1">
              <span>#</span>
              <span>{t('leaderboard.seller', 'Venditore')}</span>
              <span className="text-right">{t('leaderboard.signed', 'Firmati')}</span>
              <span className="text-right min-w-[80px]">{t('leaderboard.revenue', 'Fatturato')}</span>
            </div>
            {items.map((row, i) => {
              const isMe = myEmail && (row.email || '').toLowerCase() === myEmail;
              return (
                <div
                  key={row.email || i}
                  className={`grid grid-cols-[2rem_1fr_auto_auto] gap-2 items-center rounded-md border px-1.5 py-1.5 ${
                    isMe
                      ? 'border-[var(--gp-gold)] bg-[var(--gp-gold)]/15'
                      : 'border-border bg-[var(--surface-2)]/50'
                  }`}
                  data-testid={`leaderboard-row-${i}`}
                >
                  <span className={`w-6 h-6 grid place-items-center rounded-full border text-[11px] font-semibold ${RANK_STYLES[i] || 'bg-transparent text-muted-foreground border-transparent'}`}>
                    {i < 3 ? <Medal className="w-3.5 h-3.5" /> : i + 1}
                  </span>
                  <span className="min-w-0">
                    <span className="text-xs font-medium truncate block">
                      {row.name}
                      {isMe && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-wide bg-[var(--gp-forest)] text-[var(--gp-gold)] rounded px-1 py-0.5" data-testid="leaderboard-me-badge">
                          {t('leaderboard.you', 'Tu')}
                        </span>
                      )}
                    </span>
                    {row.role === 'sales_director' && (
                      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Director</span>
                    )}
                  </span>
                  <span className="text-xs tabular-nums font-semibold text-right" data-testid={`leaderboard-signed-${i}`}>
                    {row.contracts_signed}
                  </span>
                  <span className="text-xs tabular-nums text-right min-w-[80px]" data-testid={`leaderboard-revenue-${i}`}>
                    {eur(row.revenue)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* ★ Fase 16 — vincitore del mese precedente */}
        {data?.previous_winner && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground border-t border-border pt-2" data-testid="leaderboard-prev-winner">
            <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <span>
              {t('leaderboard.prevWinner', 'Vincitore')} {data.previous_winner.month_label}:{' '}
              <b className="text-foreground">{data.previous_winner.name}</b>
              {' · '}{data.previous_winner.contracts_signed} {t('leaderboard.signed', 'Firmati').toLowerCase()}
              {' · '}{eur(data.previous_winner.revenue)}
            </span>
          </div>
        )}
      </CardContent>

      {/* ★ Fase 16 — dialog modifica premio (Direttrice/admin) */}
      <Dialog open={prizeOpen} onOpenChange={(o) => !o && setPrizeOpen(false)}>
        <DialogContent className="max-w-md" data-testid="prize-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-[hsl(var(--primary))]" />
              {t('leaderboard.prizeDialogTitle', 'Premio del mese')} · {data?.month_label || ''}
            </DialogTitle>
            <DialogDescription>
              {t('leaderboard.prizeDialogDesc', 'Vince chi chiude più contratti firmati (a parità, il fatturato più alto). Puoi modificarlo quando vuoi.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">{t('leaderboard.prizeTitleLabel', 'Premio')}</Label>
              <Input
                value={prizeTitle}
                onChange={(e) => setPrizeTitle(e.target.value)}
                placeholder={t('leaderboard.prizePlaceholder', 'es. Cena per due + bonus €200')}
                className="mt-1"
                maxLength={140}
                data-testid="prize-title-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">{t('leaderboard.prizeDescLabel', 'Dettagli (facoltativi)')}</Label>
              <Textarea
                value={prizeDesc}
                onChange={(e) => setPrizeDesc(e.target.value)}
                rows={3}
                maxLength={1000}
                className="mt-1"
                data-testid="prize-desc-input"
              />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox
                checked={prizeNotify}
                onCheckedChange={(v) => setPrizeNotify(!!v)}
                data-testid="prize-notify-checkbox"
              />
              {t('leaderboard.prizeNotify', 'Invia subito la comunicazione email a tutto il team')}
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setPrizeOpen(false)} disabled={prizeSaving}>
              {t('common.cancel', 'Annulla')}
            </Button>
            <Button
              onClick={savePrize}
              disabled={prizeSaving}
              className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]"
              data-testid="prize-save-btn"
            >
              {prizeSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
              {t('leaderboard.prizeSave', 'Salva premio')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ★ Fase 16 — popup annuncio premio per i venditori */}
      <Dialog open={popupOpen} onOpenChange={(o) => !o && closePopup()}>
        <DialogContent className="max-w-md" data-testid="prize-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-[var(--gp-gold)]" />
              {t('leaderboard.popupTitle', 'Premio del mese')} · {data?.month_label || ''}
            </DialogTitle>
          </DialogHeader>
          {data?.prize && (
            <div className="space-y-4">
              <div className="rounded-lg border border-[var(--gp-gold)]/60 bg-[var(--gp-gold)]/10 p-4 text-center">
                <Trophy className="w-8 h-8 mx-auto text-[var(--gp-gold)]" />
                <div className="text-lg font-bold mt-2" data-testid="prize-popup-title">{data.prize.title}</div>
                {data.prize.description && (
                  <p className="text-xs text-muted-foreground mt-1">{data.prize.description}</p>
                )}
                {data.prize.set_by_name && (
                  <p className="text-[10px] text-muted-foreground mt-2">
                    {t('leaderboard.prizeSetBy', 'Messo in palio da')} {data.prize.set_by_name}
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('leaderboard.popupRules', 'Vince chi chiude più contratti firmati entro fine mese (a parità, il fatturato più alto).')}
              </p>
              {myIndex >= 0 && (
                <p className="text-sm font-medium" data-testid="prize-popup-position">
                  {t('leaderboard.popupPosition', 'La tua posizione attuale:')}{' '}
                  <span className="text-[hsl(var(--primary))]">#{myIndex + 1}</span>
                  {' · '}{items[myIndex].contracts_signed} {t('leaderboard.signed', 'Firmati').toLowerCase()}
                </p>
              )}
              {data.previous_winner && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <Crown className="w-3.5 h-3.5 text-amber-500" />
                  {t('leaderboard.prevWinner', 'Vincitore')} {data.previous_winner.month_label}:{' '}
                  <b>{data.previous_winner.name}</b>
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={closePopup}
              className="w-full bg-[var(--gp-forest)] text-[var(--gp-gold)] hover:opacity-90"
              data-testid="prize-popup-close-btn"
            >
              {t('leaderboard.popupCta', 'Ci sto: si vince!')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
